/**
 * Analyzes comments to identify potential buyers and users with friction points
 * @param {Array} comments - Array of comment objects
 * @returns {Object} - Object with categorized comments
 */

// Keywords that indicate buying intent
const BUYER_KEYWORDS = [
  'buy', 'purchase', 'price', 'cost', 'where to buy', 'how much',
  'available', 'store', 'website', 'link', 'shop', 'order',
  'interested', 'want this', 'need this', 'getting this',
  'worth it', 'should i buy', 'thinking about buying',
  'selling', 'sale', 'discount', 'coupon', 'deal',
  'checkout', 'cart', 'checking out'
];

// Keywords that indicate friction points or need for help
const FRICTION_KEYWORDS = [
  'help', 'how to', 'how do', 'stuck', 'problem', 'issue', 'error',
  'not working', "doesn't work", "won't work", 'broken', 'fix',
  'confused', 'understand', 'explain', 'question', 'why',
  'trouble', 'difficulty', 'challenge', 'struggling', 'can\'t',
  'unable', 'failed', 'failure', 'bug', 'glitch',
  'support', 'tutorial', 'guide', 'instructions', 'stuck',
  'need help', 'anyone help', 'please help'
];

// Note: QUESTION_KEYWORDS removed - now using regex patterns for better accuracy

// Keywords that indicate positive feedback
const POSITIVE_KEYWORDS = [
  'love', 'amazing', 'great', 'awesome', 'excellent', 'fantastic',
  'perfect', 'wonderful', 'brilliant', 'outstanding', 'superb',
  'thank you', 'thanks', 'appreciate', 'grateful', 'best',
  'favorite', 'favourite', 'recommend', 'recommended', '5 stars',
  'five stars', 'incredible', 'impressive', 'beautiful', 'stunning',
  'good job', 'well done', 'keep it up', 'keep up', 'keep going'
];

// Keywords that indicate suggestions
const SUGGESTION_KEYWORDS = [
  'suggest', 'suggestion', 'should', 'could', 'might', 'maybe',
  'consider', 'think about', 'idea', 'recommend', 'recommendation',
  'improve', 'improvement', 'add', 'feature', 'wish', 'hope',
  'would be nice', 'would be great', 'would love', 'it would be',
  'please add', 'please consider', 'please implement'
];

/**
 * Checks if a comment contains buyer intent keywords (with word boundaries)
 * @param {string} text - Comment text
 * @returns {boolean} - True if buyer intent detected
 */
const hasBuyerIntent = (text) => {
  if (!text || typeof text !== 'string') return false;
  const cleanText = text.replace(/<[^>]*>/g, '').trim();
  const lowerText = cleanText.toLowerCase();
  
  // Check for strong buyer indicators with word boundaries
  const strongBuyerPatterns = [
    /\b(buy|purchase|buying|purchasing)\b/i,
    /\bwhere\s+to\s+buy\b/i,
    /\bhow\s+to\s+buy\b/i,
    /\bhow\s+much\b/i,
    /\bprice\b/i,
    /\bcost\b/i,
    /\bavailable\s+(to\s+)?buy\b/i,
  ];
  
  if (strongBuyerPatterns.some(pattern => pattern.test(cleanText))) {
    return true;
  }
  
  // Check for other buyer keywords
  return BUYER_KEYWORDS.some(keyword => {
    // Use word boundaries for single words to avoid false positives
    if (keyword.length > 3 && !keyword.includes(' ')) {
      const regex = new RegExp(`\\b${keyword}\\b`, 'i');
      return regex.test(cleanText);
    }
    // For phrases, use simple includes
    return lowerText.includes(keyword.toLowerCase());
  });
};

/**
 * Checks if a comment indicates friction or need for help
 * @param {string} text - Comment text
 * @returns {boolean} - True if friction detected
 */
const hasFrictionPoint = (text) => {
  if (!text || typeof text !== 'string') return false;
  const cleanText = text.replace(/<[^>]*>/g, '').trim();
  const lowerText = cleanText.toLowerCase();
  
  // Strong friction indicators (avoid false positives)
  const strongFrictionPatterns = [
    /\b(need|needs)\s+help\b/i,
    /\b(please|can|could)\s+help\b/i,
    /\bnot\s+working\b/i,
    /\bdoesn'?t\s+work\b/i,
    /\bwon'?t\s+work\b/i,
    /\bhow\s+to\s+(fix|solve|use|do)\b/i,
    /\bhow\s+do\s+(i|you)\b/i,
    /\bwhat'?s\s+wrong\b/i,
    /\bwhat'?s\s+the\s+problem\b/i,
  ];
  
  if (strongFrictionPatterns.some(pattern => pattern.test(cleanText))) {
    return true;
  }
  
  // Check for other friction keywords with word boundaries
  return FRICTION_KEYWORDS.some(keyword => {
    if (keyword.length > 3 && !keyword.includes(' ')) {
      const regex = new RegExp(`\\b${keyword}\\b`, 'i');
      return regex.test(cleanText);
    }
    return lowerText.includes(keyword.toLowerCase());
  });
};

/**
 * Calculates a score for buyer intent (0-1)
 * @param {string} text - Comment text
 * @returns {number} - Buyer intent score
 */
const calculateBuyerScore = (text) => {
  if (!text || typeof text !== 'string') return 0;
  const cleanText = text.replace(/<[^>]*>/g, '').trim();
  const lowerText = cleanText.toLowerCase();
  let score = 0;
  
  // Strong buyer indicators (weighted heavily)
  if (/\b(buy|purchase|buying|purchasing)\b/i.test(cleanText)) {
    score += 0.4;
  }
  if (/\bwhere\s+to\s+buy\b/i.test(cleanText) || /\bhow\s+to\s+buy\b/i.test(cleanText)) {
    score += 0.5;
  }
  if (/\b(price|cost)\b/i.test(cleanText)) {
    score += 0.3;
  }
  if (/\bhow\s+much\b/i.test(cleanText)) {
    score += 0.3;
  }
  if (/\b(available|in stock|where can i get)\b/i.test(cleanText)) {
    score += 0.25;
  }
  
  // Other buyer keywords
  const buyerKeywords = ['interested', 'want this', 'need this', 'getting this', 
                         'worth it', 'should i buy', 'shop', 'store', 'website', 
                         'link', 'order', 'sale', 'discount'];
  const keywordMatches = buyerKeywords.filter(keyword => {
    if (keyword.includes(' ')) {
      return lowerText.includes(keyword);
    }
    const regex = new RegExp(`\\b${keyword}\\b`, 'i');
    return regex.test(cleanText);
  }).length;
  
  score += Math.min(keywordMatches * 0.15, 0.4);
  
  // Penalize if it's clearly a question about something else
  if (/\?/.test(cleanText) && !/\b(buy|purchase|price|cost|where|how much)\b/i.test(cleanText)) {
    score *= 0.7; // Reduce score if it's a question but not about buying
  }
  
  return Math.min(score, 1);
};

/**
 * Calculates a score for friction point (0-1)
 * @param {string} text - Comment text
 * @returns {number} - Friction score
 */
const calculateFrictionScore = (text) => {
  if (!text || typeof text !== 'string') return 0;
  const lowerText = text.toLowerCase();
  let score = 0;
  const matches = FRICTION_KEYWORDS.filter(keyword => 
    lowerText.includes(keyword.toLowerCase())
  ).length;
  score = Math.min(matches / 4, 1); // Normalize to 0-1
  
  // Boost score for urgent help requests
  if (lowerText.includes('help') || lowerText.includes('stuck')) score += 0.2;
  if (lowerText.includes('not working') || lowerText.includes('error')) score += 0.3;
  if (lowerText.includes('how to') || lowerText.includes('how do')) score += 0.2;
  
  return Math.min(score, 1);
};

/**
 * Checks if a comment is a question
 * @param {string} text - Comment text
 * @returns {boolean} - True if question detected
 */
const isQuestion = (text) => {
  if (!text || typeof text !== 'string') return false;
  // Clean text - remove HTML tags if any
  const cleanText = text.replace(/<[^>]*>/g, '').trim();
  
  // Strong indicator: Question mark at the end of a sentence
  if (/\?/.test(cleanText)) {
    // Check if it's a genuine question (not just a URL or email)
    const sentences = cleanText.split(/[.!?]+/).filter(s => s.trim());
    const lastSentence = sentences[sentences.length - 1];
    if (lastSentence && lastSentence.trim().endsWith('?')) {
      return true;
    }
  }
  
  // Question word patterns at the start of sentences
  const questionPatterns = [
    /^(what|when|where|who|why|how|can|could|would|should|will|do|does|did|is|are|was|were)\s+/i,
    /^(is|are|was|were|do|does|did|can|could|would|should|will)\s+\w+/i,
  ];
  
  if (questionPatterns.some(pattern => pattern.test(cleanText))) {
    return true;
  }
  
  // Question phrase patterns
  const questionPhrases = [
    /can you/i, /could you/i, /would you/i, /should i/i, /can i/i,
    /do you/i, /does it/i, /is it/i, /is this/i, /are you/i,
    /how do/i, /how to/i, /how can/i, /what is/i, /what are/i,
    /why is/i, /why are/i, /when is/i, /where is/i, /who is/i,
  ];
  
  return questionPhrases.some(pattern => pattern.test(cleanText));
};

/**
 * Calculates a score for question (0-1)
 * @param {string} text - Comment text
 * @returns {number} - Question score
 */
const calculateQuestionScore = (text) => {
  if (!text || typeof text !== 'string') return 0;
  const cleanText = text.replace(/<[^>]*>/g, '').trim();
  let score = 0;
  
  // Question mark at sentence end is the strongest indicator
  const sentences = cleanText.split(/[.!?]+/).filter(s => s.trim());
  const questionSentences = sentences.filter(s => s.trim().endsWith('?'));
  if (questionSentences.length > 0) {
    score += Math.min(0.6 + (questionSentences.length * 0.2), 0.9);
  }
  
  // Question word at start of sentence
  const questionStartPattern = /^(what|when|where|who|why|how|can|could|would|should|will|do|does|did|is|are|was|were)\s+/i;
  if (questionStartPattern.test(cleanText)) {
    score += 0.3;
  }
  
  // Question phrase patterns
  const questionPhrases = [
    /can you/i, /could you/i, /would you/i, /should i/i, /can i/i,
    /how do/i, /how to/i, /how can/i, /what is/i, /what are/i,
    /why is/i, /why are/i, /when is/i, /where is/i, /who is/i,
  ];
  const phraseMatches = questionPhrases.filter(pattern => pattern.test(cleanText)).length;
  score += Math.min(phraseMatches * 0.15, 0.4);
  
  // Other question keywords (weighted less)
  const otherQuestionWords = ['wondering', 'curious', 'wondered', 'ask', 'asking'];
  const wordMatches = otherQuestionWords.filter(word => {
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    return regex.test(cleanText);
  }).length;
  score += Math.min(wordMatches * 0.1, 0.2);
  
  return Math.min(score, 1);
};

/**
 * Checks if a comment is positive feedback
 * @param {string} text - Comment text
 * @returns {boolean} - True if positive feedback detected
 */
const isPositiveFeedback = (text) => {
  if (!text || typeof text !== 'string') return false;
  const cleanText = text.replace(/<[^>]*>/g, '').trim();
  
  // Strong positive indicators
  const strongPositivePatterns = [
    /\b(thank you|thanks|thank)\b/i,
    /\b(love|lovely|loved)\b/i,
    /\b(amazing|awesome|excellent|fantastic|perfect|wonderful|brilliant|outstanding|superb)\b/i,
    /\b(best|favorite|favourite)\b/i,
    /\b(5|five)\s+stars?\b/i,
    /\b(good job|well done|keep it up)\b/i,
  ];
  
  return strongPositivePatterns.some(pattern => pattern.test(cleanText));
};

/**
 * Calculates a score for positive feedback (0-1)
 * @param {string} text - Comment text
 * @returns {number} - Positive feedback score
 */
const calculatePositiveScore = (text) => {
  if (!text || typeof text !== 'string') return 0;
  const cleanText = text.replace(/<[^>]*>/g, '').trim();
  const lowerText = cleanText.toLowerCase();
  let score = 0;
  
  // Strong positive indicators
  if (/\b(thank you|thanks|thank)\b/i.test(cleanText)) score += 0.4;
  if (/\b(love|lovely|loved)\b/i.test(cleanText)) score += 0.4;
  if (/\b(amazing|awesome|excellent|fantastic|perfect|wonderful|brilliant|outstanding|superb|incredible|impressive)\b/i.test(cleanText)) {
    score += 0.4;
  }
  if (/\b(best|favorite|favourite)\b/i.test(cleanText)) score += 0.3;
  if (/\b(5|five)\s+stars?\b/i.test(cleanText)) score += 0.4;
  if (/\b(good job|well done|keep it up)\b/i.test(cleanText)) score += 0.3;
  if (/\brecommend\b/i.test(cleanText)) score += 0.25;
  
  // Other positive keywords
  const matches = POSITIVE_KEYWORDS.filter(keyword => {
    if (keyword.includes(' ')) {
      return lowerText.includes(keyword);
    }
    const regex = new RegExp(`\\b${keyword}\\b`, 'i');
    return regex.test(cleanText);
  }).length;
  
  score += Math.min(matches * 0.1, 0.3);
  
  return Math.min(score, 1);
};

/**
 * Checks if a comment is a suggestion
 * @param {string} text - Comment text
 * @returns {boolean} - True if suggestion detected
 */
const isSuggestion = (text) => {
  if (!text || typeof text !== 'string') return false;
  const cleanText = text.replace(/<[^>]*>/g, '').trim();
  
  // Strong suggestion patterns
  const suggestionPatterns = [
    /\b(suggest|suggestion|recommend|recommendation)\b/i,
    /\b(should|could|might|maybe)\s+(add|implement|consider|include)\b/i,
    /\b(please\s+)?(add|consider|implement|include)\b/i,
    /\b(it|this|that)\s+would\s+be\s+(nice|great|better|good)\b/i,
    /\b(would|it'd|it would)\s+(be|love|like)\b/i,
    /\b(idea|ideas)\s+(for|to)\b/i,
    /\b(think|thinking)\s+about\s+(add|adding|including)\b/i,
  ];
  
  return suggestionPatterns.some(pattern => pattern.test(cleanText));
};

/**
 * Calculates a score for suggestion (0-1)
 * @param {string} text - Comment text
 * @returns {number} - Suggestion score
 */
const calculateSuggestionScore = (text) => {
  if (!text || typeof text !== 'string') return 0;
  const lowerText = text.toLowerCase();
  let score = 0;
  const matches = SUGGESTION_KEYWORDS.filter(keyword => 
    lowerText.includes(keyword.toLowerCase())
  ).length;
  score = Math.min(matches / 3, 1); // Normalize to 0-1
  
  // Boost score for strong suggestion indicators
  if (lowerText.includes('suggest') || lowerText.includes('recommend')) score += 0.2;
  if (lowerText.includes('should') || lowerText.includes('could')) score += 0.2;
  if (lowerText.includes('please add') || lowerText.includes('would be')) score += 0.3;
  
  return Math.min(score, 1);
};

/**
 * Filters out low-quality comments (bad language, offensive, emoji-only, no value)
 * @param {Array} comments - Array of comment objects
 * @returns {Array} - Filtered comments
 */
const filterLowQualityComments = (comments) => {
  // Safety check: return empty array if comments is not an array
  if (!Array.isArray(comments)) {
    console.warn('filterLowQualityComments: comments is not an array, returning empty array');
    return [];
  }

  // Common profanity/offensive words (basic list - can be expanded)
  const profanityPatterns = [
    /\b(fuck|shit|damn|hell|bitch|asshole|piss|crap|dick|pussy|cunt)\b/i,
    /\b(stfu|gtfo|kys|kill yourself)\b/i,
  ];

  // Emoji-only pattern (comment is mostly or entirely emojis)
  const emojiOnlyPattern = /^[\s\p{Emoji}\p{Emoji_Presentation}\p{Emoji_Modifier_Base}]+$/u;

  return comments.filter(comment => {
    // Safety check: skip if comment is null/undefined
    if (!comment || typeof comment !== 'object') {
      return false;
    }
    const text = (comment.textDisplay || comment.textOriginal || comment.text || '').trim();
    
    // Skip empty comments
    if (!text || text.length === 0) return false;

    // Filter profanity/offensive language
    if (profanityPatterns.some(pattern => pattern.test(text))) {
      return false;
    }

    // Filter emoji-only comments (more than 70% emojis or only emojis)
    const emojiCount = (text.match(/[\p{Emoji}\p{Emoji_Presentation}\p{Emoji_Modifier_Base}]/gu) || []).length;
    const textLength = text.replace(/[\s\p{Emoji}\p{Emoji_Presentation}\p{Emoji_Modifier_Base}]/gu, '').length;
    if (emojiOnlyPattern.test(text) || (emojiCount > 0 && textLength === 0) || (emojiCount > textLength * 0.7 && textLength < 10)) {
      return false;
    }

    // Filter very short comments that don't offer value (less than 3 words, unless it's a question or has buyer intent)
    const words = text.split(/\s+/).filter(w => w.length > 0);
    if (words.length < 3 && !text.includes('?') && !/\b(buy|price|cost|where|how much)\b/i.test(text)) {
      return false;
    }

    // Filter comments that are just URLs or links without context
    const urlPattern = /^https?:\/\/\S+$/i;
    if (urlPattern.test(text.trim()) && words.length < 5) {
      return false;
    }

    return true;
  });
};

/**
 * Generates Revenue Signals summary for purchase intent analysis
 * @param {Array} potentialBuyers - Array of buyer intent comments
 * @param {Array} frictionPoints - Array of friction point comments
 * @param {number} totalComments - Total number of comments analyzed
 * @returns {Object} - Revenue Signals summary
 */
const generateRevenueSignals = (potentialBuyers, frictionPoints, totalComments) => {
  // Calculate buying intent percentage
  const buyingIntentPct = totalComments > 0 
    ? Math.round((potentialBuyers.length / totalComments) * 100) 
    : 0;

  // Get top 3 buying signals (highest buyer scores)
  const topBuyingSignals = potentialBuyers
    .slice(0, 3)
    .map(comment => ({
      text: comment.textDisplay || comment.textOriginal || comment.text || '',
      score: comment.buyerScore || 0
    }));

  // Get main objections (friction points that might block purchase)
  // Focus on price, cost, quality, availability concerns
  const objectionKeywords = ['price', 'cost', 'expensive', 'cheap', 'afford', 'worth', 'quality', 'available', 'stock', 'out of stock'];
  const objections = frictionPoints
    .filter(comment => {
      const text = (comment.textDisplay || comment.textOriginal || comment.text || '').toLowerCase();
      return objectionKeywords.some(keyword => text.includes(keyword));
    })
    .slice(0, 3)
    .map(comment => ({
      text: comment.textDisplay || comment.textOriginal || comment.text || '',
      score: comment.frictionScore || 0
    }));

  // Determine overall buying readiness
  let buyingReadiness = 'Low';
  if (buyingIntentPct >= 25 && objections.length === 0) {
    buyingReadiness = 'High';
  } else if (buyingIntentPct >= 15 || (buyingIntentPct >= 10 && objections.length <= 1)) {
    buyingReadiness = 'Medium';
  } else if (buyingIntentPct >= 5) {
    buyingReadiness = 'Low';
  } else {
    buyingReadiness = 'Low';
  }

  return {
    buyingIntentPercentage: buyingIntentPct,
    topBuyingSignals: topBuyingSignals,
    mainObjections: objections,
    buyingReadiness: buyingReadiness
  };
};

/**
 * Analyzes comments and categorizes them
 * @param {Array} comments - Array of comment objects
 * @returns {Object} - Categorized comments with metadata
 */
export const analyzeComments = (comments, totalCommentCount = null) => {
  // Filter out low-quality comments first
  const filteredComments = filterLowQualityComments(comments);
  const filteredCount = comments.length - filteredComments.length;
  if (filteredCount > 0) {
    console.log(`Filtered out ${filteredCount} low-quality comments (profanity, emoji-only, or no value)`);
  }

  const potentialBuyers = [];
  const frictionPoints = [];
  const questions = [];
  const positiveFeedback = [];
  const suggestions = [];
  const otherComments = [];

  filteredComments.forEach(comment => {
    // Get comment text from any available property
    const commentText = comment.textDisplay || comment.textOriginal || comment.text || '';
    
    const buyerScore = calculateBuyerScore(commentText);
    const frictionScore = calculateFrictionScore(commentText);
    const questionScore = calculateQuestionScore(commentText);
    const positiveScore = calculatePositiveScore(commentText);
    const suggestionScore = calculateSuggestionScore(commentText);

    const analyzedComment = {
      ...comment,
      buyerScore,
      frictionScore,
      questionScore,
      positiveScore,
      suggestionScore,
    };

    // Categorize comments with improved logic
    // Use scores first, then fallback to boolean checks
    // Priority: Questions (if high score) > Buyer > Friction > Positive > Suggestions
    
    // Questions should be checked first with higher threshold to avoid false positives
    if (questionScore >= 0.5 || (questionScore >= 0.4 && isQuestion(commentText))) {
      questions.push(analyzedComment);
    } 
    // Buyer intent (but exclude if it's clearly a question about buying)
    else if (buyerScore >= 0.35 || (buyerScore >= 0.25 && hasBuyerIntent(commentText) && questionScore < 0.4)) {
      potentialBuyers.push(analyzedComment);
    } 
    // Friction points (but exclude if it's just a question)
    else if (frictionScore >= 0.35 || (frictionScore >= 0.25 && hasFrictionPoint(commentText) && questionScore < 0.4)) {
      frictionPoints.push(analyzedComment);
    } 
    // Positive feedback
    else if (positiveScore >= 0.4 || (positiveScore >= 0.3 && isPositiveFeedback(commentText))) {
      positiveFeedback.push(analyzedComment);
    } 
    // Suggestions
    else if (suggestionScore >= 0.4 || (suggestionScore >= 0.3 && isSuggestion(commentText))) {
      suggestions.push(analyzedComment);
    } 
    else {
      otherComments.push(analyzedComment);
    }
  });

  // Sort by score (highest first)
  potentialBuyers.sort((a, b) => b.buyerScore - a.buyerScore);
  frictionPoints.sort((a, b) => b.frictionScore - a.frictionScore);
  questions.sort((a, b) => b.questionScore - a.questionScore);
  positiveFeedback.sort((a, b) => b.positiveScore - a.positiveScore);
  suggestions.sort((a, b) => b.suggestionScore - a.suggestionScore);

  // Generate comments resume/summary
  const generateCommentsResume = () => {
    const total = filteredComments.length;
    if (total === 0) {
      return {
        summary: 'No comments available for analysis.',
        sentiment: 'neutral',
        insights: [],
        metrics: {}
      };
    }

    const buyerPct = Math.round((potentialBuyers.length / total) * 100);
    const frictionPct = Math.round((frictionPoints.length / total) * 100);
    const positivePct = Math.round((positiveFeedback.length / total) * 100);
    const questionPct = Math.round((questions.length / total) * 100);
    const suggestionPct = Math.round((suggestions.length / total) * 100);
    const otherPct = Math.round((otherComments.length / total) * 100);

    // Calculate average scores for intensity
    const avgBuyerScore = potentialBuyers.length > 0
      ? potentialBuyers.reduce((sum, c) => sum + (c.buyerScore || 0), 0) / potentialBuyers.length
      : 0;
    const avgFrictionScore = frictionPoints.length > 0
      ? frictionPoints.reduce((sum, c) => sum + (c.frictionScore || 0), 0) / frictionPoints.length
      : 0;
    const avgPositiveScore = positiveFeedback.length > 0
      ? positiveFeedback.reduce((sum, c) => sum + (c.positiveScore || 0), 0) / positiveFeedback.length
      : 0;

    // Determine overall sentiment with more nuance
    let sentiment = 'neutral';
    let sentimentIntensity = 'moderate';
    let sentimentDescription = '';
    
    if (positivePct > 50) {
      sentiment = 'very positive';
      sentimentIntensity = 'strong';
      sentimentDescription = 'The community shows overwhelming positive engagement';
    } else if (positivePct > 35) {
      sentiment = 'positive';
      sentimentIntensity = 'moderate';
      sentimentDescription = 'The community response is generally favorable';
    } else if (positivePct > 20 && frictionPct < 15) {
      sentiment = 'positive';
      sentimentIntensity = 'moderate';
      sentimentDescription = 'Comments lean toward positive feedback';
    } else if (frictionPct > 35) {
      sentiment = 'concerned';
      sentimentIntensity = 'high';
      sentimentDescription = 'Significant concerns and issues are being raised';
    } else if (frictionPct > 20) {
      sentiment = 'mixed';
      sentimentIntensity = 'moderate';
      sentimentDescription = 'Mixed reactions with notable concerns';
    } else if (positivePct > 15 && frictionPct < 10) {
      sentiment = 'positive';
      sentimentIntensity = 'moderate';
      sentimentDescription = 'Generally positive with minimal concerns';
    } else if (buyerPct > 20) {
      sentiment = 'engaged';
      sentimentIntensity = 'high';
      sentimentDescription = 'High engagement with strong purchase interest';
    } else {
      sentiment = 'neutral';
      sentimentIntensity = 'moderate';
      sentimentDescription = 'Neutral community response';
    }

    // Build insights array
    const insights = [];

    // Purchase intent insights
    if (buyerPct > 0) {
      if (buyerPct > 25) {
        insights.push({
          type: 'opportunity',
          text: `Strong purchase intent: ${buyerPct}% of comments (${potentialBuyers.length}) show buying interest`,
          intensity: avgBuyerScore > 0.5 ? 'high' : 'moderate'
        });
      } else if (buyerPct > 10) {
        insights.push({
          type: 'opportunity',
          text: `Moderate purchase intent: ${buyerPct}% of comments indicate buying interest`,
          intensity: 'moderate'
        });
      } else {
        insights.push({
          type: 'info',
          text: `Limited purchase intent: ${buyerPct}% show buying interest`,
          intensity: 'low'
        });
      }
    }

    // Friction insights
    if (frictionPct > 0) {
      if (frictionPct > 25) {
        insights.push({
          type: 'warning',
          text: `High friction detected: ${frictionPct}% of comments (${frictionPoints.length}) indicate problems or need help`,
          intensity: avgFrictionScore > 0.5 ? 'high' : 'moderate'
        });
      } else if (frictionPct > 10) {
        insights.push({
          type: 'warning',
          text: `Some friction points: ${frictionPct}% of comments show concerns or need assistance`,
          intensity: 'moderate'
        });
      } else {
        insights.push({
          type: 'info',
          text: `Low friction: Only ${frictionPct}% indicate issues`,
          intensity: 'low'
        });
      }
    }

    // Positive feedback insights
    if (positivePct > 0) {
      if (positivePct > 40) {
        insights.push({
          type: 'success',
          text: `Excellent reception: ${positivePct}% of comments are highly positive`,
          intensity: avgPositiveScore > 0.5 ? 'high' : 'moderate'
        });
      } else if (positivePct > 20) {
        insights.push({
          type: 'success',
          text: `Good feedback: ${positivePct}% express positive sentiment`,
          intensity: 'moderate'
        });
      }
    }

    // Questions insights
    if (questionPct > 15) {
      insights.push({
        type: 'info',
        text: `High engagement: ${questionPct}% of comments are questions, showing active community interest`,
        intensity: 'moderate'
      });
    } else if (questionPct > 5) {
      insights.push({
        type: 'info',
        text: `Moderate engagement: ${questionPct}% of comments ask questions`,
        intensity: 'low'
      });
    }

    // Suggestions insights
    if (suggestionPct > 10) {
      insights.push({
        type: 'info',
        text: `Community input: ${suggestionPct}% provide suggestions for improvement`,
        intensity: 'moderate'
      });
    }

    // Build comprehensive summary
    let summary = `Analysis of ${total} comment${total !== 1 ? 's' : ''} reveals a ${sentiment} sentiment (${sentimentIntensity} intensity). ${sentimentDescription}. `;
    
    // Add category breakdown
    const categories = [];
    if (buyerPct > 0) categories.push(`${buyerPct}% purchase intent`);
    if (frictionPct > 0) categories.push(`${frictionPct}% friction points`);
    if (positivePct > 0) categories.push(`${positivePct}% positive feedback`);
    if (questionPct > 0) categories.push(`${questionPct}% questions`);
    if (suggestionPct > 0) categories.push(`${suggestionPct}% suggestions`);
    
    if (categories.length > 0) {
      summary += `Breakdown: ${categories.join(', ')}. `;
    }

    // Add engagement level
    const engagementLevel = questionPct + buyerPct + frictionPct;
    if (engagementLevel > 50) {
      summary += `High community engagement detected. `;
    } else if (engagementLevel > 30) {
      summary += `Moderate community engagement. `;
    }

    // Add actionable insight
    if (buyerPct > 20 && frictionPct < 10) {
      summary += `Strong opportunity: High purchase intent with low friction suggests good conversion potential.`;
    } else if (frictionPct > 20) {
      summary += `Action needed: Address friction points to improve user experience and conversion.`;
    } else if (positivePct > 30 && buyerPct > 10) {
      summary += `Positive momentum: High satisfaction combined with purchase interest indicates strong product-market fit.`;
    } else if (questionPct > 20) {
      summary += `Educational opportunity: High question volume suggests need for clearer communication or documentation.`;
    }

    return {
      summary,
      sentiment,
      sentimentIntensity,
      sentimentDescription,
      insights,
      metrics: {
        total,
        buyerPct,
        frictionPct,
        positivePct,
        questionPct,
        suggestionPct,
        otherPct,
        avgBuyerScore: Math.round(avgBuyerScore * 100) / 100,
        avgFrictionScore: Math.round(avgFrictionScore * 100) / 100,
        avgPositiveScore: Math.round(avgPositiveScore * 100) / 100,
      }
    };
  };

  // Use actual total comment count from video statistics if provided, otherwise use fetched comments count
  const actualTotalComments = totalCommentCount !== null && totalCommentCount > 0 
    ? totalCommentCount 
    : filteredComments.length;

  // Generate Revenue Signals summary
  const revenueSignals = generateRevenueSignals(potentialBuyers, frictionPoints, filteredComments.length);

  return {
    potentialBuyers,
    frictionPoints,
    questions,
    positiveFeedback,
    suggestions,
    otherComments,
    totalComments: actualTotalComments,
    fetchedComments: filteredComments.length, // Number of comments actually fetched and analyzed (after filtering)
    filteredCount: filteredCount, // Number of comments filtered out
    buyerCount: potentialBuyers.length,
    frictionCount: frictionPoints.length,
    questionCount: questions.length,
    positiveCount: positiveFeedback.length,
    suggestionCount: suggestions.length,
    commentsResume: generateCommentsResume(),
    revenueSignals: revenueSignals,
  };
};



